import type {
  AndroidPushAdministrationOverview,
  DeliveryPolicyReason,
} from "@reminder-tracker/backend-contracts";
import { useMutation, useQuery } from "convex/react";
import { Component, useRef, useState, type ReactNode } from "react";
import { ChannelPolicyReview } from "../operational-context/channel-policy-review";

import {
  getAndroidPushAdministrationOverview,
  getDeliveryCapabilities,
  setAndroidPushPolicy,
} from "../../backend/functions";
import { formatAdministrationTime } from "../../ui/administration-time";

type AndroidPushChannelMode = "disabled" | "test-only";

type Feedback = Readonly<{ kind: "error" | "success"; message: string }>;

type PendingCommand = Readonly<{
  commandKey: string;
  expectedRevision: number | null;
  mode: AndroidPushChannelMode;
  reason: DeliveryPolicyReason;
}>;

function newOpaqueKey(prefix: "cmd" | "corr"): string {
  if (typeof globalThis.crypto.randomUUID !== "function") {
    throw new Error("A secure administration correlation generator is unavailable.");
  }
  return `${prefix}_${globalThis.crypto.randomUUID().replaceAll("-", "")}`;
}

function policyRevision(overview: AndroidPushAdministrationOverview): number | null {
  return overview.policy.state === "configured" ? overview.policy.revision : null;
}

function modeLabel(mode: AndroidPushChannelMode): string {
  return mode === "disabled" ? "Disabled" : "Test only";
}

function formatPolicyTimestamp(epochMilliseconds: number): string {
  return formatAdministrationTime(epochMilliseconds);
}

export function AndroidPushGovernanceLoading(): React.JSX.Element {
  return (
    <section className="push-governance push-governance-state" aria-busy="true" aria-live="polite">
      <h2>Android push governance</h2>
      <p>Reading Android push aggregates…</p>
    </section>
  );
}

export function AndroidPushGovernanceFailure(): React.JSX.Element {
  return (
    <section className="push-governance push-governance-state" role="alert">
      <h2>Android push information unavailable</h2>
      <p>
        No registration, delivery or reminder detail has been shown. Refresh the page to try again.
      </p>
    </section>
  );
}

function isSecurityFailure(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("data" in error)) return false;
  const data = error.data;
  if (typeof data !== "object" || data === null || !("code" in data)) return false;
  return ["AUTHENTICATION_REQUIRED", "AUTHORIZATION_DENIED", "REAUTHENTICATION_REQUIRED"].includes(
    String(data.code),
  );
}

type AndroidPushGovernanceErrorBoundaryState = Readonly<{
  error?: unknown;
  hasError: boolean;
}>;

class AndroidPushGovernanceErrorBoundary extends Component<
  Readonly<{ children: ReactNode }>,
  AndroidPushGovernanceErrorBoundaryState
> {
  public override state: AndroidPushGovernanceErrorBoundaryState = { hasError: false };

  public static getDerivedStateFromError(error: unknown): AndroidPushGovernanceErrorBoundaryState {
    return { error, hasError: true };
  }

  public override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (isSecurityFailure(this.state.error)) throw this.state.error;
    return <AndroidPushGovernanceFailure />;
  }
}

export function ProtectedAndroidPushGovernance(): React.JSX.Element {
  return (
    <AndroidPushGovernanceErrorBoundary>
      <ConnectedAndroidPushGovernance />
    </AndroidPushGovernanceErrorBoundary>
  );
}

function ConnectedAndroidPushGovernance(): React.JSX.Element {
  const [correlationId] = useState(() => newOpaqueKey("corr"));
  const overview = useQuery(getAndroidPushAdministrationOverview, { correlationId });
  const capabilities = useQuery(getDeliveryCapabilities, { correlationId });
  const setPolicy = useMutation(setAndroidPushPolicy);
  const pendingCommand = useRef<PendingCommand | null>(null);
  const [pendingMode, setPendingMode] = useState<AndroidPushChannelMode | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  if (overview === undefined) return <AndroidPushGovernanceLoading />;

  const changeMode = async (
    mode: AndroidPushChannelMode,
    reason: DeliveryPolicyReason,
  ): Promise<void> => {
    if (overview.policy.state === "configured" && overview.policy.mode === mode) {
      setFeedback({ kind: "success", message: `Android push is already ${modeLabel(mode)}.` });
      return;
    }

    const expectedRevision = policyRevision(overview);
    const retained = pendingCommand.current;
    let command: PendingCommand;
    if (
      retained !== null &&
      retained.mode === mode &&
      retained.reason === reason &&
      retained.expectedRevision === expectedRevision
    ) {
      command = retained;
    } else {
      command = { commandKey: newOpaqueKey("cmd"), expectedRevision, mode, reason };
    }
    pendingCommand.current = command;
    setPendingMode(mode);
    setFeedback(null);

    try {
      const result = await setPolicy({
        commandKey: command.commandKey,
        correlationId: newOpaqueKey("corr"),
        expectedRevision: command.expectedRevision,
        mode: command.mode,
        reason: command.reason,
      });
      pendingCommand.current = null;
      setFeedback({
        kind: "success",
        message: `Android push changed to ${modeLabel(result.mode)} at policy revision ${result.revision}.`,
      });
    } catch {
      setFeedback({
        kind: "error",
        message:
          "The policy was not confirmed. Refresh the aggregate view, then retry the same choice if needed.",
      });
    } finally {
      setPendingMode(null);
    }
  };

  return (
    <AndroidPushGovernanceView
      canManagePolicy={capabilities?.canManagePolicy === true}
      overview={overview}
      pendingMode={pendingMode}
      feedback={feedback}
      onChangeMode={(mode, reason) => void changeMode(mode, reason)}
    />
  );
}

export function AndroidPushGovernanceView({
  overview,
  pendingMode,
  feedback,
  onChangeMode,
  canManagePolicy = false,
}: Readonly<{
  overview: AndroidPushAdministrationOverview;
  pendingMode: AndroidPushChannelMode | null;
  feedback: Feedback | null;
  onChangeMode: (mode: AndroidPushChannelMode, reason: DeliveryPolicyReason) => void;
  canManagePolicy?: boolean;
}>): React.JSX.Element {
  const currentMode = overview.policy.state === "configured" ? overview.policy.mode : null;
  const policyRecordedAt =
    overview.policy.state === "configured"
      ? new Date(overview.policy.updatedAtEpochMilliseconds).toISOString()
      : null;

  return (
    <section className="push-governance" aria-labelledby="android-push-title">
      <div className="section-head push-governance-head">
        <div>
          <h2 id="android-push-title">Android push governance</h2>
          <p>
            Shared Test can stop Android push or permit bounded synthetic delivery. In-app reminders
            stay available in either mode.
          </p>
        </div>
        <span className="push-environment">{overview.environment}</span>
      </div>

      <div className="push-policy">
        <div>
          <span className="push-policy-label">Current policy</span>
          <strong>{currentMode === null ? "Not configured" : modeLabel(currentMode)}</strong>
          {overview.policy.state === "configured" ? (
            <small>
              Revision {overview.policy.revision} ·{" "}
              <time dateTime={policyRecordedAt ?? undefined}>
                {formatPolicyTimestamp(overview.policy.updatedAtEpochMilliseconds)}
              </time>
            </small>
          ) : (
            <small>No outbound Android push policy has been admitted in this environment.</small>
          )}
        </div>
        <ChannelPolicyReview
          channel="Android push"
          environment={overview.environment}
          currentMode={currentMode}
          revision={overview.policy.state === "configured" ? overview.policy.revision : null}
          pendingMode={pendingMode}
          onConfirm={onChangeMode}
          canManagePolicy={canManagePolicy}
        />
      </div>

      {feedback === null ? null : (
        <p
          className={`push-feedback ${feedback.kind}`}
          role={feedback.kind === "error" ? "alert" : "status"}
        >
          {feedback.message}
        </p>
      )}

      <div className="push-aggregate-groups">
        <AggregateGroup
          title="Device registrations"
          values={[
            ["Active registrations", overview.activeRegistrationCount],
            ["Permission denied", overview.permissionDeniedRegistrationCount],
            ["Revoked registrations", overview.revokedRegistrationCount],
            ["Invalid registrations", overview.invalidTokenRegistrationCount],
          ]}
        />
        <AggregateGroup
          title="Delivery work"
          values={[
            ["Pending intents", overview.pendingIntentCount],
            ["Suppressed intents", overview.suppressedIntentCount],
            ["Accepted by Expo", overview.acceptedAttemptCount],
            ["Delivered to FCM", overview.deliveredAttemptCount],
            ["Failed attempts", overview.failedAttemptCount],
            ["Unknown after receipt wait", overview.unknownAttemptCount],
          ]}
        />
      </div>

      <div className="push-outcome-note">
        <strong>What delivery means</strong>
        <p>
          Accepted means Expo accepted the request. Delivered means an authenticated receipt reached
          FCM; it does not prove that a phone displayed or a person read a reminder. Unknown is kept
          distinct so the service does not resend blindly.
        </p>
      </div>

      <dl className="push-privacy-proof">
        <div>
          <dt>Reminder content accessible</dt>
          <dd>No</dd>
        </div>
        <div>
          <dt>Administration detail</dt>
          <dd>Aggregate counts only</dd>
        </div>
      </dl>
    </section>
  );
}

function AggregateGroup({
  title,
  values,
}: Readonly<{
  title: string;
  values: ReadonlyArray<readonly [label: string, value: number]>;
}>): React.JSX.Element {
  return (
    <section className="push-aggregate-group" aria-label={title}>
      <h3>{title}</h3>
      <dl>
        {values.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
